import React, {
    startTransition,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react'

import {
    Button,
    ButtonGroup,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Tooltip,
    Typography,
} from '@material-ui/core'

import Box from '@mui/material/Box'
import DeleteIcon from '@material-ui/icons/Delete'

import { Schedule } from '../../../models'
import {
    PayrollWorksheet,
    PayrollWorksheetDay,
    PayrollWorksheetEmployee,
    PayrollWorksheetEmployeeDay,
    computePayrollTotal,
    getWorksheetsBySchedule,
    getWorksheet,
    generateFromSchedule,
    generateWeekDays,
    createWorksheetEmployee,
    createEmployeeDay,
    deleteWorksheetEmployee,
    updateEmployeeDay,
    updateWorksheet,
    updateWorksheetEmployee,
} from '../../../models/PayrollWorksheet'
import AddIcon from '@material-ui/icons/Add'
import GetAppIcon from '@material-ui/icons/GetApp'
import SettingsIcon from '@material-ui/icons/Settings'
import FileDownload from 'js-file-download'

import { toast } from 'react-toastify'

interface Props {
    scheduleDetail: Schedule | undefined
    onPayrollTotalChange?: (total: number) => void
    portfolio?: boolean
}

const REG_HOURS_THRESHOLD = 40

const formatMoney = (value: string | number | null | undefined) => {
    if (value == null || value === '') return '$0.00'
    const num = typeof value === 'string' ? parseFloat(value) : value
    return (
        '$' +
        num.toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        })
    )
}

const getDayWithDate = (dateStr: string) => {
    const parts = dateStr.split('-').map(Number)
    const d = new Date(parts[0], parts[1] - 1, parts[2])
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
    const date = d.toLocaleDateString('en-US', {
        month: 'numeric',
        day: 'numeric',
    })
    return `${days[d.getDay()]} ${date}`
}

const formatDateLocal = (dateStr: string) => {
    const parts = dateStr.split('-').map(Number)
    const d = new Date(parts[0], parts[1] - 1, parts[2])
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

const getWeekDateRange = (days: PayrollWorksheetDay[]) => {
    if (!days?.length) return ''
    const first = days[0].date
    const last = days[days.length - 1].date
    if (!first || !last) return ''
    return `${formatDateLocal(first)} – ${formatDateLocal(last)}`
}

const escapeCsvCell = (val: string | number | null | undefined): string => {
    const s = String(val ?? '')
    if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`
    return s
}

/** Normalize API value for staff_type single-select (unknown → unspecified). */
function payrollStaffTypeSelectValue(
    staffType: string | null | undefined,
): '' | 'admin' | 'maintenance' {
    if (staffType === 'admin' || staffType === 'maintenance') return staffType
    return ''
}

type PayrollStaffTypeFilter = 'all' | 'admin' | 'maintenance'

type PayrollHoursViewMode = 'projected' | 'actual' | 'both'

function employeeMatchesStaffTypeFilter(
    emp: PayrollWorksheetEmployee,
    filter: PayrollStaffTypeFilter,
): boolean {
    if (filter === 'all') return true
    return payrollStaffTypeSelectValue(emp.staff_type) === filter
}

function staffTypeLabelForCsv(staffType: string | null | undefined): string {
    const v = payrollStaffTypeSelectValue(staffType)
    if (v === 'admin') return 'Admin'
    if (v === 'maintenance') return 'Maintenance'
    return 'Unspecified'
}

function buildPayrollCsv(
    worksheet: PayrollWorksheet,
    formatMoneyFn: (v: string | number | null | undefined) => string,
    getDayWithDateFn: (dateStr: string) => string,
    regHoursThreshold: number,
): string {
    const roster = (worksheet.employees ?? []).sort(
        (a, b) => a.position - b.position,
    )
    const weeks = worksheet.weeks ?? []
    const rows: string[][] = []

    const headerRow1: string[] = ['', '', '', '', '']
    const headerRow2: string[] = [
        'Employee',
        'Staff type',
        'On Salary?',
        'Regular Rate',
        'OT Rate',
    ]
    weeks.forEach((week) => {
        const days = week.days ?? []
        if (days.length === 0) {
            headerRow1.push(week.name ?? '')
            headerRow2.push('—')
            return
        }
        const weekLabelProj = `${week.name ?? ''} • ${getWeekDateRange(
            days,
        )} — Projected`
        const weekLabelAct = `${week.name ?? ''} • ${getWeekDateRange(
            days,
        )} — Actual`
        headerRow1.push(weekLabelProj)
        for (let i = 0; i < days.length + 6 - 1; i++) headerRow1.push('')
        headerRow1.push(weekLabelAct)
        for (let i = 0; i < days.length + 6 - 1; i++) headerRow1.push('')
        days.forEach((d) => headerRow2.push(getDayWithDateFn(d.date)))
        headerRow2.push(
            'Total Hrs',
            'Reg Hrs',
            'OT Hrs',
            'Reg Pay',
            'OT Pay',
            'Proj Total',
        )
        days.forEach((d) => headerRow2.push(getDayWithDateFn(d.date)))
        headerRow2.push(
            'Total Hrs',
            'Reg Hrs',
            'OT Hrs',
            'Reg Pay',
            'OT Pay',
            'Actual Total',
        )
    })
    headerRow1.push('', '', 'Total Summary', '', '', '')
    headerRow2.push(
        'Reg Pay',
        'OT Pay',
        'Proj Total',
        'Reg Pay',
        'OT Pay',
        'Actual Total',
    )
    rows.push(headerRow1.map(escapeCsvCell))
    rows.push(headerRow2.map(escapeCsvCell))

    roster.forEach((emp) => {
        const isSalary = emp.on_salary
        const regRate = parseFloat(emp.regular_rate || '0')
        const otRate = isSalary ? 0 : parseFloat(emp.overtime_rate || '0')
        const row: string[] = [
            escapeCsvCell(emp.employee_name ?? emp.display_name ?? ''),
            escapeCsvCell(staffTypeLabelForCsv(emp.staff_type)),
            escapeCsvCell(emp.on_salary ? 'Yes' : 'No'),
            escapeCsvCell(emp.regular_rate ?? ''),
            escapeCsvCell(emp.overtime_rate ?? ''),
        ]
        weeks.forEach((week) => {
            const days = week.days ?? []
            const weekEmp = week.employees?.find((e) => e.id === emp.id)
            const dayMap = new Map(
                (weekEmp?.day_entries ?? []).map((de) => [
                    de.worksheet_day,
                    de,
                ]),
            )
            if (days.length === 0) {
                row.push('')
                return
            }
            days.forEach((day) => {
                const entry = dayMap.get(day.id)
                row.push(entry?.projected_hours ?? '0')
            })
            const projectedTotal = (weekEmp?.day_entries ?? []).reduce(
                (s, d) => s + parseFloat(d.projected_hours || '0'),
                0,
            )
            const actualTotal = (weekEmp?.day_entries ?? []).reduce(
                (s, d) => s + parseFloat(d.actual_hours || '0'),
                0,
            )
            const projectedReg = Math.min(projectedTotal, regHoursThreshold)
            const projectedOt = isSalary
                ? 0
                : Math.max(0, projectedTotal - regHoursThreshold)
            const actualReg = Math.min(actualTotal, regHoursThreshold)
            const actualOt = isSalary
                ? 0
                : Math.max(0, actualTotal - regHoursThreshold)
            const wt = weekEmp?.week_totals
            const projectedRegPay =
                wt?.projected_reg_pay != null && wt.projected_reg_pay !== ''
                    ? parseFloat(wt.projected_reg_pay)
                    : projectedReg * regRate
            const projectedOtPay = isSalary
                ? 0
                : wt?.projected_ot_pay != null && wt.projected_ot_pay !== ''
                ? parseFloat(wt.projected_ot_pay)
                : projectedOt * otRate
            const actualRegPay =
                wt?.actual_reg_pay != null && wt.actual_reg_pay !== ''
                    ? parseFloat(wt.actual_reg_pay)
                    : actualReg * regRate
            const actualOtPay = isSalary
                ? 0
                : wt?.actual_ot_pay != null && wt.actual_ot_pay !== ''
                ? parseFloat(wt.actual_ot_pay)
                : actualOt * otRate
            const projectedTotalPay = projectedRegPay + projectedOtPay
            const actualTotalPay = actualRegPay + actualOtPay
            row.push(
                projectedTotal.toFixed(1),
                projectedReg.toFixed(1),
                projectedOt.toFixed(1),
                escapeCsvCell(formatMoneyFn(projectedRegPay)),
                escapeCsvCell(formatMoneyFn(projectedOtPay)),
                escapeCsvCell(formatMoneyFn(projectedTotalPay)),
            )
            days.forEach((day) => {
                const entry = dayMap.get(day.id)
                row.push(entry?.actual_hours ?? '0')
            })
            row.push(
                actualTotal.toFixed(1),
                actualReg.toFixed(1),
                actualOt.toFixed(1),
                escapeCsvCell(formatMoneyFn(actualRegPay)),
                escapeCsvCell(formatMoneyFn(actualOtPay)),
                escapeCsvCell(formatMoneyFn(actualTotalPay)),
            )
        })
        const projTotal = emp.projected_total_pay ?? ''
        const actTotal = emp.actual_total_pay ?? ''
        row.push(
            escapeCsvCell(formatMoneyFn(emp.projected_reg_pay)),
            escapeCsvCell(formatMoneyFn(emp.projected_ot_pay)),
            escapeCsvCell(formatMoneyFn(projTotal)),
            escapeCsvCell(formatMoneyFn(emp.actual_reg_pay)),
            escapeCsvCell(formatMoneyFn(emp.actual_ot_pay)),
            escapeCsvCell(formatMoneyFn(actTotal)),
        )
        rows.push(row)
    })

    // Total row (same structure as table tfoot)
    const totalRow: string[] = ['Total', '', '', '', '']
    let allProjReg = 0
    let allProjOt = 0
    let allActReg = 0
    let allActOt = 0
    weeks.forEach((week) => {
        const days = week.days ?? []
        if (days.length === 0) {
            totalRow.push('')
            return
        }
        const employees = week.employees ?? []
        const weekTotals = employees.reduce(
            (acc, emp) => {
                const isSalary = emp.on_salary
                const wt = emp.week_totals
                const regRate = parseFloat(emp.regular_rate || '0')
                const otRate = isSalary
                    ? 0
                    : parseFloat(emp.overtime_rate || '0')
                const projectedTotal = (emp.day_entries ?? []).reduce(
                    (s, d) => s + parseFloat(d.projected_hours || '0'),
                    0,
                )
                const actualTotal = (emp.day_entries ?? []).reduce(
                    (s, d) => s + parseFloat(d.actual_hours || '0'),
                    0,
                )
                const projectedReg = Math.min(projectedTotal, regHoursThreshold)
                const projectedOt = isSalary
                    ? 0
                    : Math.max(0, projectedTotal - regHoursThreshold)
                const actualRegComputed = Math.min(
                    actualTotal,
                    regHoursThreshold,
                )
                const actualOtComputed = isSalary
                    ? 0
                    : Math.max(0, actualTotal - regHoursThreshold)
                const projRegPayStored = parseFloat(wt?.projected_reg_pay ?? '')
                const projOtPayStored = isSalary
                    ? 0
                    : parseFloat(wt?.projected_ot_pay ?? '')
                const actRegPayStored = parseFloat(wt?.actual_reg_pay ?? '')
                const actOtPayStored = isSalary
                    ? 0
                    : parseFloat(wt?.actual_ot_pay ?? '')
                let projRegHrs = parseFloat(wt?.projected_reg_hours ?? '')
                const projOtHrs = isSalary
                    ? 0
                    : parseFloat(wt?.projected_ot_hours ?? '')
                let actRegHrs = parseFloat(wt?.actual_reg_hours ?? '')
                const actOtHrs = isSalary
                    ? 0
                    : parseFloat(wt?.actual_ot_hours ?? '')
                if (isSalary) {
                    projRegHrs = Math.min(
                        Number.isNaN(projRegHrs) ? 0 : projRegHrs,
                        regHoursThreshold,
                    )
                    actRegHrs = Math.min(
                        Number.isNaN(actRegHrs) ? 0 : actRegHrs,
                        regHoursThreshold,
                    )
                }
                const useStoredProjPay =
                    !Number.isNaN(projRegPayStored) &&
                    (!Number.isNaN(projOtPayStored) || isSalary)
                const useStoredActPay =
                    !Number.isNaN(actRegPayStored) &&
                    (!Number.isNaN(actOtPayStored) || isSalary)
                const useStoredProjHrs =
                    !Number.isNaN(projRegHrs) &&
                    (!Number.isNaN(projOtHrs) || isSalary)
                const useStoredActHrs =
                    !Number.isNaN(actRegHrs) &&
                    (!Number.isNaN(actOtHrs) || isSalary)
                const projectedRegPay = useStoredProjPay
                    ? projRegPayStored
                    : projectedReg * regRate
                const projectedOtPay = useStoredProjPay
                    ? projOtPayStored
                    : projectedOt * otRate
                const actualRegPay = useStoredActPay
                    ? actRegPayStored
                    : useStoredActHrs
                    ? actRegHrs * regRate
                    : actualRegComputed * regRate
                const actualOtPay = useStoredActPay
                    ? actOtPayStored
                    : useStoredActHrs
                    ? actOtHrs * otRate
                    : actualOtComputed * otRate
                const actualReg = useStoredActPay
                    ? actRegHrs
                    : useStoredActHrs
                    ? actRegHrs
                    : actualRegComputed
                const actualOt = useStoredActPay
                    ? actOtHrs
                    : useStoredActHrs
                    ? actOtHrs
                    : actualOtComputed
                const projectedRegHrs = useStoredProjHrs
                    ? projRegHrs
                    : projectedReg
                const projectedOtHrsVal = useStoredProjHrs
                    ? projOtHrs
                    : projectedOt
                return {
                    projectedTotalHrs: acc.projectedTotalHrs + projectedTotal,
                    projectedRegHrs: acc.projectedRegHrs + projectedRegHrs,
                    projectedOtHrs: acc.projectedOtHrs + projectedOtHrsVal,
                    projectedRegPay: acc.projectedRegPay + projectedRegPay,
                    projectedOtPay: acc.projectedOtPay + projectedOtPay,
                    actualTotalHrs: acc.actualTotalHrs + actualTotal,
                    actualRegHrs: acc.actualRegHrs + actualReg,
                    actualOtHrs: acc.actualOtHrs + actualOt,
                    actualRegPay: acc.actualRegPay + actualRegPay,
                    actualOtPay: acc.actualOtPay + actualOtPay,
                }
            },
            {
                projectedTotalHrs: 0,
                projectedRegHrs: 0,
                projectedOtHrs: 0,
                projectedRegPay: 0,
                projectedOtPay: 0,
                actualTotalHrs: 0,
                actualRegHrs: 0,
                actualOtHrs: 0,
                actualRegPay: 0,
                actualOtPay: 0,
            },
        )
        days.forEach(() => totalRow.push(''))
        totalRow.push(
            weekTotals.projectedTotalHrs.toFixed(1),
            weekTotals.projectedRegHrs.toFixed(1),
            weekTotals.projectedOtHrs.toFixed(1),
            escapeCsvCell(formatMoneyFn(weekTotals.projectedRegPay)),
            escapeCsvCell(formatMoneyFn(weekTotals.projectedOtPay)),
            escapeCsvCell(
                formatMoneyFn(
                    weekTotals.projectedRegPay + weekTotals.projectedOtPay,
                ),
            ),
        )
        days.forEach(() => totalRow.push(''))
        totalRow.push(
            weekTotals.actualTotalHrs.toFixed(1),
            weekTotals.actualRegHrs.toFixed(1),
            weekTotals.actualOtHrs.toFixed(1),
            escapeCsvCell(formatMoneyFn(weekTotals.actualRegPay)),
            escapeCsvCell(formatMoneyFn(weekTotals.actualOtPay)),
            escapeCsvCell(
                formatMoneyFn(weekTotals.actualRegPay + weekTotals.actualOtPay),
            ),
        )
        // Accumulate for grand total
        allProjReg += weekTotals.projectedRegPay
        allProjOt += weekTotals.projectedOtPay
        allActReg += weekTotals.actualRegPay
        allActOt += weekTotals.actualOtPay
    })
    totalRow.push(
        escapeCsvCell(formatMoneyFn(allProjReg)),
        escapeCsvCell(formatMoneyFn(allProjOt)),
        escapeCsvCell(formatMoneyFn(allProjReg + allProjOt)),
        escapeCsvCell(formatMoneyFn(allActReg)),
        escapeCsvCell(formatMoneyFn(allActOt)),
        escapeCsvCell(formatMoneyFn(allActReg + allActOt)),
    )
    rows.push(totalRow)

    return rows.map((r) => r.join(',')).join('\r\n')
}

function applyDayEntryUpdate(
    prev: PayrollWorksheet,
    employeeId: number,
    worksheetDayId: number,
    field: 'projected_hours' | 'actual_hours',
    value: string,
): PayrollWorksheet {
    const weeks = (prev.weeks ?? []).map((week) => {
        const dayIds = (week.days ?? []).map((d) => d.id)
        if (!dayIds.includes(worksheetDayId)) return week
        const employees = (week.employees ?? []).map((emp) => {
            if (emp.id !== employeeId) return emp
            const day_entries = (emp.day_entries ?? []).map((de) =>
                de.worksheet_day === worksheetDayId
                    ? { ...de, [field]: value }
                    : de,
            )
            const hasEntry = day_entries.some(
                (de) => de.worksheet_day === worksheetDayId,
            )
            if (!hasEntry) {
                day_entries.push({
                    id: 0,
                    employee: employeeId,
                    worksheet_day: worksheetDayId,
                    projected_hours: field === 'projected_hours' ? value : '0',
                    actual_hours: field === 'actual_hours' ? value : '0',
                })
            }
            return { ...emp, day_entries }
        })
        return { ...week, employees }
    })
    return { ...prev, weeks }
}

function applyEmployeeUpdate(
    prev: PayrollWorksheet,
    employeeId: number,
    field:
        | 'employee_name'
        | 'staff_type'
        | 'on_salary'
        | 'regular_rate'
        | 'overtime_rate',
    value: string | boolean,
): PayrollWorksheet {
    const update = (emp: PayrollWorksheetEmployee) =>
        emp.id === employeeId ? { ...emp, [field]: value } : emp
    const employees = (prev.employees ?? []).map(update)
    const weeks = (prev.weeks ?? []).map((week) => ({
        ...week,
        employees: (week.employees ?? []).map(update),
    }))
    return { ...prev, employees, weeks }
}

interface PayrollGridProps {
    worksheet: PayrollWorksheet
    onDayChange: (
        worksheetId: number,
        employee: PayrollWorksheetEmployee,
        dayEntry: PayrollWorksheetEmployeeDay,
        field: 'projected_hours' | 'actual_hours',
        value: string,
    ) => void
    onEmployeeChange: (
        worksheetId: number,
        employee: PayrollWorksheetEmployee,
        field:
            | 'employee_name'
            | 'staff_type'
            | 'on_salary'
            | 'regular_rate'
            | 'overtime_rate',
        value: string | boolean,
    ) => void
    onRemoveEmployee?: (employee: PayrollWorksheetEmployee) => void
    onGenerateWeekDays?: (weekId: number, numDays: number) => void
    regHoursThreshold: number
    formatMoney: (v: string | number | null | undefined) => string
    getDayWithDate: (dateStr: string) => string
    staffTypeFilter: PayrollStaffTypeFilter
    hoursViewMode: PayrollHoursViewMode
}

const cellStyle = {
    padding: '2px 4px',
    border: '1px solid #e0e0e0',
    fontSize: '0.8rem',
    verticalAlign: 'middle' as const,
    overflow: 'visible' as const,
    whiteSpace: 'nowrap' as const,
}

const editableCellBg = '#fffde7'

/** Payroll grid sticky columns — widths must match <colgroup>. */
const PAYROLL_FW_EMPLOYEE = 160
const PAYROLL_FW_STAFF_TYPE = 112
const PAYROLL_FW_ON_SALARY = 90
const PAYROLL_FW_REGULAR = 100
const PAYROLL_FW_OVERTIME = 95

const PAYROLL_LEFT_STAFF_TYPE = PAYROLL_FW_EMPLOYEE
const PAYROLL_LEFT_ON_SALARY = PAYROLL_FW_EMPLOYEE + PAYROLL_FW_STAFF_TYPE
const PAYROLL_LEFT_REGULAR = PAYROLL_LEFT_ON_SALARY + PAYROLL_FW_ON_SALARY
const PAYROLL_LEFT_OVERTIME = PAYROLL_LEFT_REGULAR + PAYROLL_FW_REGULAR

const stickyLeft = (left: number, bg: string, shadow?: boolean) => ({
    ...cellStyle,
    border: 'none',
    position: 'sticky' as const,
    left,
    zIndex: 2,
    backgroundColor: bg,
    boxShadow: shadow
        ? `4px 0 0 0 ${bg}, 6px 0 6px -2px rgba(0,0,0,0.15)`
        : `4px 0 0 0 ${bg}`,
})

function PayrollGrid({
    worksheet,
    onDayChange,
    onEmployeeChange,
    onRemoveEmployee,
    onGenerateWeekDays,
    regHoursThreshold,
    formatMoney,
    getDayWithDate,
    staffTypeFilter,
    hoursViewMode,
}: PayrollGridProps) {
    const [localEdit, setLocalEdit] = useState<{
        empId: number
        field: 'employee_name' | 'regular_rate' | 'overtime_rate'
        value: string
    } | null>(null)
    const [editingDayCell, setEditingDayCell] = useState<{
        empId: number
        weekId: number
        dayId: number
        field: 'projected_hours' | 'actual_hours'
        value: string
    } | null>(null)
    const [generateNumDays, setGenerateNumDays] = useState(7)

    const sortedRoster = useMemo(
        () =>
            [...(worksheet.employees || [])].sort(
                (a, b) => a.position - b.position,
            ),
        [worksheet.employees],
    )
    const roster = useMemo(
        () =>
            sortedRoster.filter((emp) =>
                employeeMatchesStaffTypeFilter(emp, staffTypeFilter),
            ),
        [sortedRoster, staffTypeFilter],
    )
    const rosterIdSet = useMemo(() => new Set(roster.map((e) => e.id)), [
        roster,
    ])
    const weeks = worksheet.weeks || []
    const showProjected = hoursViewMode !== 'actual'
    const showActual = hoursViewMode !== 'projected'
    const summaryHeaderColSpan = (showProjected ? 3 : 0) + (showActual ? 3 : 0)

    const splitSections = showProjected && showActual

    /** Background-only tints (no borderColor) so toggling splitSections does not
     *  fight `cellStyle`'s `border` shorthand — avoids a full-grid border flash. */
    const projBannerBg: React.CSSProperties = splitSections
        ? { backgroundColor: '#d6e2f4' }
        : {}

    const actBannerBg: React.CSSProperties = splitSections
        ? { backgroundColor: '#cde8cd' }
        : {}

    const projColHeaderBg: React.CSSProperties = splitSections
        ? { backgroundColor: '#eef3fb' }
        : {}

    const actColHeaderBg: React.CSSProperties = splitSections
        ? { backgroundColor: '#f1faf1' }
        : {}

    const projBodyBand: React.CSSProperties = splitSections
        ? { backgroundColor: '#f5f8fd' }
        : {}

    const actBodyBand: React.CSSProperties = splitSections
        ? { backgroundColor: '#f7fcf7' }
        : {}

    const projEditableBand: React.CSSProperties = splitSections
        ? { backgroundColor: '#eff6ff' }
        : { backgroundColor: editableCellBg }

    const actEditableBand: React.CSSProperties = splitSections
        ? { backgroundColor: '#f4fff6' }
        : { backgroundColor: editableCellBg }

    const projTotalEmphasisBg: React.CSSProperties = splitSections
        ? { backgroundColor: '#e3f2eb' }
        : { backgroundColor: '#e8f5e9' }

    const actTotalEmphasisBg: React.CSSProperties = splitSections
        ? { backgroundColor: '#ddeef8' }
        : { backgroundColor: '#e8f5e9' }

    /** Inset stripe only — avoids changing border width/color vs cellStyle.border */
    const sectionDividerLeft: React.CSSProperties = splitSections
        ? { boxShadow: 'inset 3px 0 0 #546e7a' }
        : {}

    const footProjBand: React.CSSProperties = splitSections
        ? { backgroundColor: '#e8f0fc' }
        : {}

    const footActBand: React.CSSProperties = splitSections
        ? { backgroundColor: '#eaf6ea' }
        : {}

    const footProjStrong: React.CSSProperties = splitSections
        ? { backgroundColor: '#d6e8fc' }
        : { backgroundColor: '#bbdefb' }

    const footActStrong: React.CSSProperties = splitSections
        ? { backgroundColor: '#cfe9cf' }
        : { backgroundColor: '#bbdefb' }

    const summaryProjBg: React.CSSProperties = splitSections
        ? { backgroundColor: '#e8eaf6' }
        : { backgroundColor: '#e8eaf6' }

    const summaryActBg: React.CSSProperties = splitSections
        ? { backgroundColor: '#e8f5e9' }
        : { backgroundColor: '#e8eaf6' }

    const summaryProjTotalBg: React.CSSProperties = splitSections
        ? { backgroundColor: '#d9dff2' }
        : { backgroundColor: '#c5cae9' }

    const summaryActTotalBg: React.CSSProperties = splitSections
        ? { backgroundColor: '#d4ead8' }
        : { backgroundColor: '#c5cae9' }

    const inputCellProps = {
        min: 0,
        step: 0.1,
        style: {
            width: '100%',
            minWidth: 48,
            border: 'none',
            background: 'transparent',
            fontSize: 'inherit',
            outline: 'none',
            textAlign: 'right' as const,
            boxSizing: 'border-box' as const,
        },
    }

    return (
        <Box
            sx={{
                flex: 1,
                minWidth: 0,
                overflowX: 'auto',
                overflowY: 'auto',
                backgroundColor: '#fff',
                '&::-webkit-scrollbar': { width: 14, height: 14 },
                '&::-webkit-scrollbar-track': {
                    backgroundColor: '#f1f1f1',
                    borderRadius: 7,
                },
                '&::-webkit-scrollbar-thumb': {
                    backgroundColor: '#b0b0b0',
                    borderRadius: 7,
                    '&:hover': { backgroundColor: '#909090' },
                },
            }}
        >
            {roster.length === 0 &&
                sortedRoster.length > 0 &&
                staffTypeFilter !== 'all' && (
                    <Typography
                        variant="caption"
                        color="textSecondary"
                        component="div"
                        style={{ padding: '4px 8px 2px' }}
                    >
                        No employees match this staff type filter.
                    </Typography>
                )}
            <table
                style={{
                    borderCollapse: 'collapse',
                    tableLayout: 'fixed',
                    width: 'max-content',
                    minWidth: 200,
                    position: 'relative',
                    zIndex: 2,
                }}
            >
                <colgroup>
                    <col style={{ width: PAYROLL_FW_EMPLOYEE }} />
                    <col style={{ width: PAYROLL_FW_STAFF_TYPE }} />
                    <col style={{ width: PAYROLL_FW_ON_SALARY }} />
                    <col style={{ width: PAYROLL_FW_REGULAR }} />
                    <col style={{ width: PAYROLL_FW_OVERTIME }} />
                    {weeks.map((week) =>
                        (week.days?.length ?? 0) > 0 ? (
                            <React.Fragment key={week.id}>
                                {showProjected && (
                                    <>
                                        {week.days!.map((d) => (
                                            <col
                                                key={`w${week.id}-p-${d.id}`}
                                                style={{ width: 76 }}
                                            />
                                        ))}
                                        <col style={{ width: 80 }} />
                                        <col style={{ width: 76 }} />
                                        <col style={{ width: 72 }} />
                                        <col style={{ width: 100 }} />
                                        <col style={{ width: 100 }} />
                                        <col style={{ width: 110 }} />
                                    </>
                                )}
                                {showActual && (
                                    <>
                                        {week.days!.map((d) => (
                                            <col
                                                key={`w${week.id}-a-${d.id}`}
                                                style={{ width: 76 }}
                                            />
                                        ))}
                                        <col style={{ width: 80 }} />
                                        <col style={{ width: 76 }} />
                                        <col style={{ width: 72 }} />
                                        <col style={{ width: 100 }} />
                                        <col style={{ width: 100 }} />
                                        <col style={{ width: 110 }} />
                                    </>
                                )}
                            </React.Fragment>
                        ) : (
                            <col
                                key={`w${week.id}-placeholder`}
                                style={{ width: 120 }}
                            />
                        ),
                    )}
                    {/* Total Summary */}
                    {showProjected && (
                        <>
                            <col style={{ width: 100 }} />
                            <col style={{ width: 100 }} />
                            <col style={{ width: 110 }} />
                        </>
                    )}
                    {showActual && (
                        <>
                            <col style={{ width: 100 }} />
                            <col style={{ width: 100 }} />
                            <col style={{ width: 110 }} />
                        </>
                    )}
                    <col style={{ width: 48 }} />
                </colgroup>
                <thead>
                    <tr style={{ backgroundColor: '#eceff1' }}>
                        <th
                            colSpan={5}
                            style={{
                                ...cellStyle,
                                border: 'none',
                                position: 'sticky',
                                left: 0,
                                zIndex: 5,
                                backgroundColor: '#eceff1',
                                boxShadow:
                                    '2px 0 0 0 #eceff1, 4px 0 6px -2px rgba(0,0,0,0.15)',
                                textAlign: 'left',
                                fontWeight: 600,
                                whiteSpace: 'nowrap',
                            }}
                        />
                        {weeks.map((week) => {
                            const numDayCols = week.days?.length ?? 0
                            const dateRange = getWeekDateRange(week.days ?? [])
                            if (numDayCols === 0) {
                                return (
                                    <th
                                        key={week.id}
                                        colSpan={1}
                                        style={{
                                            ...cellStyle,
                                            textAlign: 'center',
                                            fontWeight: 600,
                                        }}
                                    >
                                        {week.name}
                                    </th>
                                )
                            }
                            const weekLabel = dateRange
                                ? `${week.name} • ${dateRange}`
                                : week.name
                            return (
                                <React.Fragment key={week.id}>
                                    {showProjected && (
                                        <th
                                            colSpan={numDayCols + 6}
                                            style={{
                                                ...cellStyle,
                                                ...projBannerBg,
                                                textAlign: 'center',
                                                fontWeight: 600,
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            {weekLabel} — Projected
                                        </th>
                                    )}
                                    {showActual && (
                                        <th
                                            colSpan={numDayCols + 6}
                                            style={{
                                                ...cellStyle,
                                                ...actBannerBg,
                                                ...sectionDividerLeft,
                                                textAlign: 'center',
                                                fontWeight: 600,
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            {weekLabel} — Actual
                                        </th>
                                    )}
                                </React.Fragment>
                            )
                        })}
                        <th
                            colSpan={summaryHeaderColSpan}
                            style={{
                                ...cellStyle,
                                textAlign: 'center',
                                fontWeight: 600,
                                whiteSpace: 'nowrap',
                                backgroundColor: '#e8eaf6',
                            }}
                        >
                            Total Summary
                        </th>
                        <th style={{ ...cellStyle, width: 48, padding: 4 }} />
                    </tr>
                    <tr style={{ backgroundColor: '#f5f5f5' }}>
                        <th
                            style={{
                                ...stickyLeft(0, '#f5f5f5'),
                                textAlign: 'left',
                                fontWeight: 600,
                                whiteSpace: 'nowrap',
                                zIndex: 5,
                            }}
                        >
                            Employee
                        </th>
                        <th
                            style={{
                                ...stickyLeft(
                                    PAYROLL_LEFT_STAFF_TYPE,
                                    '#f5f5f5',
                                ),
                                textAlign: 'center',
                                fontWeight: 600,
                                whiteSpace: 'nowrap',
                                zIndex: 5,
                            }}
                        >
                            Staff type
                        </th>
                        <th
                            style={{
                                ...stickyLeft(
                                    PAYROLL_LEFT_ON_SALARY,
                                    '#f5f5f5',
                                ),
                                textAlign: 'center',
                                fontWeight: 600,
                                whiteSpace: 'nowrap',
                                zIndex: 5,
                            }}
                        >
                            On Salary?
                        </th>
                        <th
                            style={{
                                ...stickyLeft(PAYROLL_LEFT_REGULAR, '#f5f5f5'),
                                textAlign: 'right',
                                fontWeight: 600,
                                whiteSpace: 'nowrap',
                                zIndex: 5,
                            }}
                        >
                            Regular Rate
                        </th>
                        <th
                            style={{
                                ...stickyLeft(
                                    PAYROLL_LEFT_OVERTIME,
                                    '#f5f5f5',
                                    true,
                                ),
                                textAlign: 'right',
                                fontWeight: 600,
                                whiteSpace: 'nowrap',
                                zIndex: 5,
                            }}
                        >
                            OT Rate
                        </th>
                        {weeks.map((week) => {
                            const days = week.days ?? []
                            if (days.length === 0) {
                                return (
                                    <th
                                        key={week.id}
                                        style={{
                                            ...cellStyle,
                                            textAlign: 'center',
                                            fontWeight: 600,
                                        }}
                                    >
                                        —
                                    </th>
                                )
                            }
                            return (
                                <React.Fragment key={week.id}>
                                    {showProjected && (
                                        <>
                                            {days.map((day) => (
                                                <th
                                                    key={day.id}
                                                    style={{
                                                        ...cellStyle,
                                                        ...projColHeaderBg,
                                                        textAlign: 'center',
                                                        fontWeight: 600,
                                                        whiteSpace: 'nowrap',
                                                    }}
                                                >
                                                    {getDayWithDate(day.date)}
                                                </th>
                                            ))}
                                            <th
                                                style={{
                                                    ...cellStyle,
                                                    ...projColHeaderBg,
                                                    textAlign: 'right',
                                                    fontWeight: 600,
                                                    whiteSpace: 'nowrap',
                                                }}
                                            >
                                                Total Hrs
                                            </th>
                                            <th
                                                style={{
                                                    ...cellStyle,
                                                    ...projColHeaderBg,
                                                    textAlign: 'right',
                                                    fontWeight: 600,
                                                    whiteSpace: 'nowrap',
                                                }}
                                            >
                                                Reg Hrs
                                            </th>
                                            <th
                                                style={{
                                                    ...cellStyle,
                                                    ...projColHeaderBg,
                                                    textAlign: 'right',
                                                    fontWeight: 600,
                                                    whiteSpace: 'nowrap',
                                                }}
                                            >
                                                OT Hrs
                                            </th>
                                            <th
                                                style={{
                                                    ...cellStyle,
                                                    ...projColHeaderBg,
                                                    textAlign: 'right',
                                                    fontWeight: 600,
                                                    whiteSpace: 'nowrap',
                                                }}
                                            >
                                                Reg Pay
                                            </th>
                                            <th
                                                style={{
                                                    ...cellStyle,
                                                    ...projColHeaderBg,
                                                    textAlign: 'right',
                                                    fontWeight: 600,
                                                    whiteSpace: 'nowrap',
                                                }}
                                            >
                                                OT Pay
                                            </th>
                                            <th
                                                style={{
                                                    ...cellStyle,
                                                    ...projColHeaderBg,
                                                    textAlign: 'right',
                                                    fontWeight: 600,
                                                    ...projTotalEmphasisBg,
                                                    whiteSpace: 'nowrap',
                                                }}
                                            >
                                                Proj Total
                                            </th>
                                        </>
                                    )}
                                    {showActual && (
                                        <>
                                            {days.map((day, dayIdx) => (
                                                <th
                                                    key={`a-${day.id}`}
                                                    style={{
                                                        ...cellStyle,
                                                        ...actColHeaderBg,
                                                        ...(dayIdx === 0
                                                            ? sectionDividerLeft
                                                            : {}),
                                                        textAlign: 'center',
                                                        fontWeight: 600,
                                                        whiteSpace: 'nowrap',
                                                    }}
                                                >
                                                    {getDayWithDate(day.date)}
                                                </th>
                                            ))}
                                            <th
                                                style={{
                                                    ...cellStyle,
                                                    ...actColHeaderBg,
                                                    textAlign: 'right',
                                                    fontWeight: 600,
                                                    whiteSpace: 'nowrap',
                                                }}
                                            >
                                                Total Hrs
                                            </th>
                                            <th
                                                style={{
                                                    ...cellStyle,
                                                    ...actColHeaderBg,
                                                    textAlign: 'right',
                                                    fontWeight: 600,
                                                    whiteSpace: 'nowrap',
                                                }}
                                            >
                                                Reg Hrs
                                            </th>
                                            <th
                                                style={{
                                                    ...cellStyle,
                                                    ...actColHeaderBg,
                                                    textAlign: 'right',
                                                    fontWeight: 600,
                                                    whiteSpace: 'nowrap',
                                                }}
                                            >
                                                OT Hrs
                                            </th>
                                            <th
                                                style={{
                                                    ...cellStyle,
                                                    ...actColHeaderBg,
                                                    textAlign: 'right',
                                                    fontWeight: 600,
                                                    whiteSpace: 'nowrap',
                                                }}
                                            >
                                                Reg Pay
                                            </th>
                                            <th
                                                style={{
                                                    ...cellStyle,
                                                    ...actColHeaderBg,
                                                    textAlign: 'right',
                                                    fontWeight: 600,
                                                    whiteSpace: 'nowrap',
                                                }}
                                            >
                                                OT Pay
                                            </th>
                                            <th
                                                style={{
                                                    ...cellStyle,
                                                    ...actColHeaderBg,
                                                    textAlign: 'right',
                                                    fontWeight: 600,
                                                    ...actTotalEmphasisBg,
                                                    whiteSpace: 'nowrap',
                                                }}
                                            >
                                                Actual Total
                                            </th>
                                        </>
                                    )}
                                </React.Fragment>
                            )
                        })}
                        {showProjected && (
                            <>
                                <th
                                    style={{
                                        ...cellStyle,
                                        ...summaryProjBg,
                                        textAlign: 'right',
                                        fontWeight: 600,
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    Reg Pay
                                </th>
                                <th
                                    style={{
                                        ...cellStyle,
                                        ...summaryProjBg,
                                        textAlign: 'right',
                                        fontWeight: 600,
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    OT Pay
                                </th>
                                <th
                                    style={{
                                        ...cellStyle,
                                        ...summaryProjTotalBg,
                                        textAlign: 'right',
                                        fontWeight: 600,
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    Proj Total
                                </th>
                            </>
                        )}
                        {showActual && (
                            <>
                                <th
                                    style={{
                                        ...cellStyle,
                                        ...summaryActBg,
                                        ...sectionDividerLeft,
                                        textAlign: 'right',
                                        fontWeight: 600,
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    Reg Pay
                                </th>
                                <th
                                    style={{
                                        ...cellStyle,
                                        ...summaryActBg,
                                        textAlign: 'right',
                                        fontWeight: 600,
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    OT Pay
                                </th>
                                <th
                                    style={{
                                        ...cellStyle,
                                        ...summaryActTotalBg,
                                        textAlign: 'right',
                                        fontWeight: 600,
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    Actual Total
                                </th>
                            </>
                        )}
                        <th style={{ ...cellStyle, width: 48, padding: 4 }} />
                    </tr>
                </thead>
                <tbody>
                    {roster.map((emp, rowIndex) => {
                        const rowBg = rowIndex % 2 === 0 ? '#fff' : '#fafafa'
                        return (
                            <tr key={emp.id} style={{ backgroundColor: rowBg }}>
                                <td
                                    style={{
                                        ...stickyLeft(0, editableCellBg),
                                        textAlign: 'left',
                                        zIndex: 4,
                                    }}
                                >
                                    <input
                                        type="text"
                                        value={
                                            localEdit?.empId === emp.id &&
                                            localEdit?.field === 'employee_name'
                                                ? localEdit.value
                                                : emp.employee_name ??
                                                  emp.display_name ??
                                                  ''
                                        }
                                        onChange={(e) =>
                                            setLocalEdit({
                                                empId: emp.id,
                                                field: 'employee_name',
                                                value: e.target.value,
                                            })
                                        }
                                        onBlur={() => {
                                            if (
                                                localEdit?.empId === emp.id &&
                                                localEdit?.field ===
                                                    'employee_name'
                                            ) {
                                                const serverVal =
                                                    emp.employee_name ?? ''
                                                if (
                                                    localEdit.value !==
                                                    serverVal
                                                ) {
                                                    onEmployeeChange(
                                                        worksheet.id,
                                                        emp,
                                                        'employee_name',
                                                        localEdit.value,
                                                    )
                                                }
                                                setLocalEdit(null)
                                            }
                                        }}
                                        placeholder="Employee name"
                                        style={{
                                            width: '100%',
                                            border: 'none',
                                            background: 'transparent',
                                            fontSize: 'inherit',
                                            outline: 'none',
                                            boxSizing: 'border-box',
                                        }}
                                    />
                                </td>
                                <td
                                    style={{
                                        ...stickyLeft(
                                            PAYROLL_LEFT_STAFF_TYPE,
                                            editableCellBg,
                                        ),
                                        textAlign: 'center',
                                        zIndex: 4,
                                    }}
                                >
                                    <select
                                        value={payrollStaffTypeSelectValue(
                                            emp.staff_type,
                                        )}
                                        onChange={(e) =>
                                            onEmployeeChange(
                                                worksheet.id,
                                                emp,
                                                'staff_type',
                                                e.target.value,
                                            )
                                        }
                                        aria-label="Staff type"
                                        style={{
                                            width: '100%',
                                            maxWidth: '100%',
                                            border: 'none',
                                            background: 'transparent',
                                            fontSize: 'inherit',
                                            cursor: 'pointer',
                                        }}
                                    >
                                        <option value="">Unspecified</option>
                                        <option value="admin">Admin</option>
                                        <option value="maintenance">
                                            Maintenance
                                        </option>
                                    </select>
                                </td>
                                <td
                                    style={{
                                        ...stickyLeft(
                                            PAYROLL_LEFT_ON_SALARY,
                                            editableCellBg,
                                        ),
                                        textAlign: 'center',
                                        zIndex: 4,
                                    }}
                                >
                                    <select
                                        value={emp.on_salary ? 'yes' : 'no'}
                                        onChange={(e) =>
                                            onEmployeeChange(
                                                worksheet.id,
                                                emp,
                                                'on_salary',
                                                e.target.value === 'yes',
                                            )
                                        }
                                        style={{
                                            width: '100%',
                                            border: 'none',
                                            background: 'transparent',
                                            fontSize: 'inherit',
                                            cursor: 'pointer',
                                        }}
                                    >
                                        <option value="yes">Yes</option>
                                        <option value="no">No</option>
                                    </select>
                                </td>
                                <td
                                    style={{
                                        ...stickyLeft(
                                            PAYROLL_LEFT_REGULAR,
                                            editableCellBg,
                                        ),
                                        textAlign: 'right',
                                        zIndex: 4,
                                    }}
                                >
                                    <input
                                        type="number"
                                        value={
                                            localEdit?.empId === emp.id &&
                                            localEdit?.field === 'regular_rate'
                                                ? localEdit.value
                                                : emp.regular_rate ?? ''
                                        }
                                        onChange={(e) =>
                                            setLocalEdit({
                                                empId: emp.id,
                                                field: 'regular_rate',
                                                value: e.target.value,
                                            })
                                        }
                                        onBlur={() => {
                                            if (
                                                localEdit?.empId === emp.id &&
                                                localEdit?.field ===
                                                    'regular_rate'
                                            ) {
                                                const serverVal =
                                                    emp.regular_rate ?? ''
                                                if (
                                                    localEdit.value !==
                                                    serverVal
                                                ) {
                                                    onEmployeeChange(
                                                        worksheet.id,
                                                        emp,
                                                        'regular_rate',
                                                        localEdit.value,
                                                    )
                                                }
                                                setLocalEdit(null)
                                            }
                                        }}
                                        placeholder="0"
                                        min={0}
                                        step={0.01}
                                        style={{
                                            width: '100%',
                                            minWidth: 56,
                                            border: 'none',
                                            background: 'transparent',
                                            fontSize: 'inherit',
                                            outline: 'none',
                                            textAlign: 'right',
                                            boxSizing: 'border-box',
                                        }}
                                    />
                                </td>
                                <td
                                    style={{
                                        ...stickyLeft(
                                            PAYROLL_LEFT_OVERTIME,
                                            editableCellBg,
                                            true,
                                        ),
                                        textAlign: 'right',
                                        zIndex: 4,
                                    }}
                                >
                                    <input
                                        type="number"
                                        value={
                                            localEdit?.empId === emp.id &&
                                            localEdit?.field === 'overtime_rate'
                                                ? localEdit.value
                                                : emp.overtime_rate ?? ''
                                        }
                                        onChange={(e) =>
                                            setLocalEdit({
                                                empId: emp.id,
                                                field: 'overtime_rate',
                                                value: e.target.value,
                                            })
                                        }
                                        onBlur={() => {
                                            if (
                                                localEdit?.empId === emp.id &&
                                                localEdit?.field ===
                                                    'overtime_rate'
                                            ) {
                                                const serverVal =
                                                    emp.overtime_rate ?? ''
                                                if (
                                                    localEdit.value !==
                                                    serverVal
                                                ) {
                                                    onEmployeeChange(
                                                        worksheet.id,
                                                        emp,
                                                        'overtime_rate',
                                                        localEdit.value,
                                                    )
                                                }
                                                setLocalEdit(null)
                                            }
                                        }}
                                        placeholder="0"
                                        min={0}
                                        step={0.01}
                                        style={{
                                            width: '100%',
                                            minWidth: 56,
                                            border: 'none',
                                            background: 'transparent',
                                            fontSize: 'inherit',
                                            outline: 'none',
                                            textAlign: 'right',
                                            boxSizing: 'border-box',
                                        }}
                                    />
                                </td>
                                {weeks.map((week) => {
                                    const days = week.days ?? []
                                    const weekEmp = week.employees?.find(
                                        (e) => e.id === emp.id,
                                    )
                                    const dayMap = new Map(
                                        (
                                            weekEmp?.day_entries ?? []
                                        ).map((de) => [de.worksheet_day, de]),
                                    )
                                    if (days.length === 0) {
                                        return (
                                            <td
                                                key={week.id}
                                                style={{
                                                    ...cellStyle,
                                                    textAlign: 'center',
                                                }}
                                            >
                                                {onGenerateWeekDays && (
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        onClick={() =>
                                                            onGenerateWeekDays(
                                                                week.id,
                                                                generateNumDays,
                                                            )
                                                        }
                                                    >
                                                        Generate days
                                                    </Button>
                                                )}
                                            </td>
                                        )
                                    }
                                    const projectedTotal = (
                                        weekEmp?.day_entries ?? []
                                    ).reduce(
                                        (s, d) =>
                                            s +
                                            parseFloat(
                                                d.projected_hours || '0',
                                            ),
                                        0,
                                    )
                                    const actualTotal = (
                                        weekEmp?.day_entries ?? []
                                    ).reduce(
                                        (s, d) =>
                                            s +
                                            parseFloat(d.actual_hours || '0'),
                                        0,
                                    )
                                    const isSalary = emp.on_salary
                                    const regRate = parseFloat(
                                        emp.regular_rate || '0',
                                    )
                                    const otRate = isSalary
                                        ? 0
                                        : parseFloat(emp.overtime_rate || '0')
                                    const projectedReg = isSalary
                                        ? Math.min(
                                              projectedTotal,
                                              regHoursThreshold,
                                          )
                                        : Math.min(
                                              projectedTotal,
                                              regHoursThreshold,
                                          )
                                    const projectedOt = isSalary
                                        ? 0
                                        : Math.max(
                                              0,
                                              projectedTotal -
                                                  regHoursThreshold,
                                          )
                                    const actualReg = isSalary
                                        ? Math.min(
                                              actualTotal,
                                              regHoursThreshold,
                                          )
                                        : Math.min(
                                              actualTotal,
                                              regHoursThreshold,
                                          )
                                    const actualOt = isSalary
                                        ? 0
                                        : Math.max(
                                              0,
                                              actualTotal - regHoursThreshold,
                                          )
                                    const wt = weekEmp?.week_totals
                                    const projectedRegPay =
                                        wt?.projected_reg_pay != null &&
                                        wt.projected_reg_pay !== ''
                                            ? parseFloat(wt.projected_reg_pay)
                                            : projectedReg * regRate
                                    const projectedOtPay = isSalary
                                        ? 0
                                        : wt?.projected_ot_pay != null &&
                                          wt.projected_ot_pay !== ''
                                        ? parseFloat(wt.projected_ot_pay)
                                        : projectedOt * otRate
                                    const actualRegPay =
                                        wt?.actual_reg_pay != null &&
                                        wt.actual_reg_pay !== ''
                                            ? parseFloat(wt.actual_reg_pay)
                                            : actualReg * regRate
                                    const actualOtPay = isSalary
                                        ? 0
                                        : wt?.actual_ot_pay != null &&
                                          wt.actual_ot_pay !== ''
                                        ? parseFloat(wt.actual_ot_pay)
                                        : actualOt * otRate
                                    return (
                                        <React.Fragment key={week.id}>
                                            {showProjected && (
                                                <>
                                                    {days.map((day) => {
                                                        const entry = dayMap.get(
                                                            day.id,
                                                        ) ?? {
                                                            id: 0,
                                                            employee: emp.id,
                                                            worksheet_day:
                                                                day.id,
                                                            projected_hours:
                                                                '0',
                                                            actual_hours: '0',
                                                        }
                                                        const isEditingProj =
                                                            editingDayCell?.empId ===
                                                                emp.id &&
                                                            editingDayCell?.weekId ===
                                                                week.id &&
                                                            editingDayCell?.dayId ===
                                                                day.id &&
                                                            editingDayCell?.field ===
                                                                'projected_hours'
                                                        return (
                                                            <td
                                                                key={day.id}
                                                                style={{
                                                                    ...cellStyle,
                                                                    ...projBodyBand,
                                                                    ...projEditableBand,
                                                                    textAlign:
                                                                        'right',
                                                                }}
                                                            >
                                                                <input
                                                                    type="number"
                                                                    value={
                                                                        isEditingProj
                                                                            ? editingDayCell!
                                                                                  .value
                                                                            : entry.projected_hours ||
                                                                              ''
                                                                    }
                                                                    onChange={(
                                                                        e,
                                                                    ) =>
                                                                        setEditingDayCell(
                                                                            {
                                                                                empId:
                                                                                    emp.id,
                                                                                weekId:
                                                                                    week.id,
                                                                                dayId:
                                                                                    day.id,
                                                                                field:
                                                                                    'projected_hours',
                                                                                value:
                                                                                    e
                                                                                        .target
                                                                                        .value,
                                                                            },
                                                                        )
                                                                    }
                                                                    onBlur={() => {
                                                                        if (
                                                                            isEditingProj
                                                                        ) {
                                                                            const val = editingDayCell!
                                                                                .value
                                                                            const strVal =
                                                                                parseFloat(
                                                                                    val,
                                                                                ) ===
                                                                                0
                                                                                    ? '0'
                                                                                    : val
                                                                            onDayChange(
                                                                                worksheet.id,
                                                                                emp,
                                                                                entry,
                                                                                'projected_hours',
                                                                                strVal,
                                                                            )
                                                                            setEditingDayCell(
                                                                                null,
                                                                            )
                                                                        }
                                                                    }}
                                                                    {...inputCellProps}
                                                                />
                                                            </td>
                                                        )
                                                    })}
                                                    <td
                                                        style={{
                                                            ...cellStyle,
                                                            ...projBodyBand,
                                                            textAlign: 'right',
                                                            fontWeight:
                                                                projectedOt > 0
                                                                    ? 'bold'
                                                                    : 'normal',
                                                        }}
                                                    >
                                                        {projectedTotal.toFixed(
                                                            1,
                                                        )}
                                                    </td>
                                                    <td
                                                        style={{
                                                            ...cellStyle,
                                                            ...projBodyBand,
                                                            textAlign: 'right',
                                                        }}
                                                    >
                                                        {weekEmp?.week_totals
                                                            ?.projected_reg_hours ??
                                                            projectedReg.toFixed(
                                                                1,
                                                            )}
                                                    </td>
                                                    <td
                                                        style={{
                                                            ...cellStyle,
                                                            ...projBodyBand,
                                                            textAlign: 'right',
                                                            fontWeight:
                                                                projectedOt > 0
                                                                    ? 'bold'
                                                                    : 'normal',
                                                        }}
                                                    >
                                                        {weekEmp?.week_totals
                                                            ?.projected_ot_hours ??
                                                            projectedOt.toFixed(
                                                                1,
                                                            )}
                                                    </td>
                                                    <td
                                                        style={{
                                                            ...cellStyle,
                                                            ...projBodyBand,
                                                            textAlign: 'right',
                                                        }}
                                                    >
                                                        {formatMoney(
                                                            projectedRegPay,
                                                        )}
                                                    </td>
                                                    <td
                                                        style={{
                                                            ...cellStyle,
                                                            ...projBodyBand,
                                                            textAlign: 'right',
                                                        }}
                                                    >
                                                        {formatMoney(
                                                            projectedOtPay,
                                                        )}
                                                    </td>
                                                    <td
                                                        style={{
                                                            ...cellStyle,
                                                            ...projBodyBand,
                                                            ...projTotalEmphasisBg,
                                                            textAlign: 'right',
                                                        }}
                                                    >
                                                        {formatMoney(
                                                            projectedRegPay +
                                                                projectedOtPay,
                                                        )}
                                                    </td>
                                                </>
                                            )}
                                            {showActual && (
                                                <>
                                                    {days.map((day, dayIdx) => {
                                                        const entry = dayMap.get(
                                                            day.id,
                                                        ) ?? {
                                                            id: 0,
                                                            employee: emp.id,
                                                            worksheet_day:
                                                                day.id,
                                                            projected_hours:
                                                                '0',
                                                            actual_hours: '0',
                                                        }
                                                        const isEditingAct =
                                                            editingDayCell?.empId ===
                                                                emp.id &&
                                                            editingDayCell?.weekId ===
                                                                week.id &&
                                                            editingDayCell?.dayId ===
                                                                day.id &&
                                                            editingDayCell?.field ===
                                                                'actual_hours'
                                                        return (
                                                            <td
                                                                key={`a-${day.id}`}
                                                                style={{
                                                                    ...cellStyle,
                                                                    ...actBodyBand,
                                                                    ...actEditableBand,
                                                                    ...(dayIdx ===
                                                                    0
                                                                        ? sectionDividerLeft
                                                                        : {}),
                                                                    textAlign:
                                                                        'right',
                                                                }}
                                                            >
                                                                <input
                                                                    type="number"
                                                                    value={
                                                                        isEditingAct
                                                                            ? editingDayCell!
                                                                                  .value
                                                                            : entry.actual_hours ||
                                                                              ''
                                                                    }
                                                                    onChange={(
                                                                        e,
                                                                    ) =>
                                                                        setEditingDayCell(
                                                                            {
                                                                                empId:
                                                                                    emp.id,
                                                                                weekId:
                                                                                    week.id,
                                                                                dayId:
                                                                                    day.id,
                                                                                field:
                                                                                    'actual_hours',
                                                                                value:
                                                                                    e
                                                                                        .target
                                                                                        .value,
                                                                            },
                                                                        )
                                                                    }
                                                                    onBlur={() => {
                                                                        if (
                                                                            isEditingAct
                                                                        ) {
                                                                            const val = editingDayCell!
                                                                                .value
                                                                            const strVal =
                                                                                parseFloat(
                                                                                    val,
                                                                                ) ===
                                                                                0
                                                                                    ? '0'
                                                                                    : val
                                                                            onDayChange(
                                                                                worksheet.id,
                                                                                emp,
                                                                                entry,
                                                                                'actual_hours',
                                                                                strVal,
                                                                            )
                                                                            setEditingDayCell(
                                                                                null,
                                                                            )
                                                                        }
                                                                    }}
                                                                    {...inputCellProps}
                                                                />
                                                            </td>
                                                        )
                                                    })}
                                                    <td
                                                        style={{
                                                            ...cellStyle,
                                                            ...actBodyBand,
                                                            textAlign: 'right',
                                                            fontWeight:
                                                                actualOt > 0
                                                                    ? 'bold'
                                                                    : 'normal',
                                                        }}
                                                    >
                                                        {actualTotal.toFixed(1)}
                                                    </td>
                                                    <td
                                                        style={{
                                                            ...cellStyle,
                                                            ...actBodyBand,
                                                            textAlign: 'right',
                                                        }}
                                                    >
                                                        {weekEmp?.week_totals
                                                            ?.actual_reg_hours ??
                                                            actualReg.toFixed(
                                                                1,
                                                            )}
                                                    </td>
                                                    <td
                                                        style={{
                                                            ...cellStyle,
                                                            ...actBodyBand,
                                                            textAlign: 'right',
                                                            fontWeight:
                                                                actualOt > 0
                                                                    ? 'bold'
                                                                    : 'normal',
                                                        }}
                                                    >
                                                        {weekEmp?.week_totals
                                                            ?.actual_ot_hours ??
                                                            actualOt.toFixed(1)}
                                                    </td>
                                                    <td
                                                        style={{
                                                            ...cellStyle,
                                                            ...actBodyBand,
                                                            textAlign: 'right',
                                                        }}
                                                    >
                                                        {formatMoney(
                                                            actualRegPay,
                                                        )}
                                                    </td>
                                                    <td
                                                        style={{
                                                            ...cellStyle,
                                                            ...actBodyBand,
                                                            textAlign: 'right',
                                                        }}
                                                    >
                                                        {formatMoney(
                                                            actualOtPay,
                                                        )}
                                                    </td>
                                                    <td
                                                        style={{
                                                            ...cellStyle,
                                                            ...actBodyBand,
                                                            ...actTotalEmphasisBg,
                                                            textAlign: 'right',
                                                        }}
                                                    >
                                                        {formatMoney(
                                                            actualRegPay +
                                                                actualOtPay,
                                                        )}
                                                    </td>
                                                </>
                                            )}
                                        </React.Fragment>
                                    )
                                })}
                                {(() => {
                                    let allProjReg = 0
                                    let allProjOt = 0
                                    let allActReg = 0
                                    let allActOt = 0
                                    const isSalary = emp.on_salary
                                    const regRate = parseFloat(
                                        emp.regular_rate || '0',
                                    )
                                    const otRate = isSalary
                                        ? 0
                                        : parseFloat(emp.overtime_rate || '0')
                                    weeks.forEach((week) => {
                                        const weekEmp = week.employees?.find(
                                            (e) => e.id === emp.id,
                                        )
                                        if (!weekEmp) return
                                        const wt = weekEmp.week_totals
                                        const entries =
                                            weekEmp.day_entries ?? []
                                        const projTotal = entries.reduce(
                                            (s, d) =>
                                                s +
                                                parseFloat(
                                                    d.projected_hours || '0',
                                                ),
                                            0,
                                        )
                                        const actTotal = entries.reduce(
                                            (s, d) =>
                                                s +
                                                parseFloat(
                                                    d.actual_hours || '0',
                                                ),
                                            0,
                                        )
                                        const projRegPayStored = parseFloat(
                                            wt?.projected_reg_pay ?? '',
                                        )
                                        const projOtPayStored = isSalary
                                            ? 0
                                            : parseFloat(
                                                  wt?.projected_ot_pay ?? '',
                                              )
                                        const actRegPayStored = parseFloat(
                                            wt?.actual_reg_pay ?? '',
                                        )
                                        const actOtPayStored = isSalary
                                            ? 0
                                            : parseFloat(
                                                  wt?.actual_ot_pay ?? '',
                                              )
                                        let actRegHrs = parseFloat(
                                            wt?.actual_reg_hours ?? '',
                                        )
                                        const actOtHrs = isSalary
                                            ? 0
                                            : parseFloat(
                                                  wt?.actual_ot_hours ?? '',
                                              )
                                        if (
                                            isSalary &&
                                            !Number.isNaN(actRegHrs)
                                        ) {
                                            actRegHrs = Math.min(
                                                actRegHrs,
                                                regHoursThreshold,
                                            )
                                        }
                                        const useStoredProj =
                                            !Number.isNaN(projRegPayStored) &&
                                            (!Number.isNaN(projOtPayStored) ||
                                                isSalary)
                                        const useStoredAct =
                                            !Number.isNaN(actRegPayStored) &&
                                            (!Number.isNaN(actOtPayStored) ||
                                                isSalary)
                                        const useStoredActHrs =
                                            !Number.isNaN(actRegHrs) &&
                                            (!Number.isNaN(actOtHrs) ||
                                                isSalary)
                                        if (useStoredProj) {
                                            allProjReg += projRegPayStored
                                            allProjOt += isSalary
                                                ? 0
                                                : projOtPayStored
                                        } else {
                                            const projReg = Math.min(
                                                projTotal,
                                                regHoursThreshold,
                                            )
                                            const projOt = isSalary
                                                ? 0
                                                : Math.max(
                                                      0,
                                                      projTotal -
                                                          regHoursThreshold,
                                                  )
                                            allProjReg += projReg * regRate
                                            allProjOt += projOt * otRate
                                        }
                                        if (useStoredAct) {
                                            allActReg += actRegPayStored
                                            allActOt += isSalary
                                                ? 0
                                                : actOtPayStored
                                        } else if (useStoredActHrs) {
                                            allActReg += actRegHrs * regRate
                                            allActOt += actOtHrs * otRate
                                        } else {
                                            const actReg = Math.min(
                                                actTotal,
                                                regHoursThreshold,
                                            )
                                            const actOt = isSalary
                                                ? 0
                                                : Math.max(
                                                      0,
                                                      actTotal -
                                                          regHoursThreshold,
                                                  )
                                            allActReg += actReg * regRate
                                            allActOt += actOt * otRate
                                        }
                                    })
                                    return (
                                        <>
                                            {showProjected && (
                                                <>
                                                    <td
                                                        style={{
                                                            ...cellStyle,
                                                            ...summaryProjBg,
                                                            textAlign: 'right',
                                                        }}
                                                    >
                                                        {formatMoney(
                                                            allProjReg,
                                                        )}
                                                    </td>
                                                    <td
                                                        style={{
                                                            ...cellStyle,
                                                            ...summaryProjBg,
                                                            textAlign: 'right',
                                                        }}
                                                    >
                                                        {formatMoney(allProjOt)}
                                                    </td>
                                                    <td
                                                        style={{
                                                            ...cellStyle,
                                                            ...summaryProjTotalBg,
                                                            textAlign: 'right',
                                                        }}
                                                    >
                                                        {formatMoney(
                                                            allProjReg +
                                                                allProjOt,
                                                        )}
                                                    </td>
                                                </>
                                            )}
                                            {showActual && (
                                                <>
                                                    <td
                                                        style={{
                                                            ...cellStyle,
                                                            ...summaryActBg,
                                                            ...sectionDividerLeft,
                                                            textAlign: 'right',
                                                        }}
                                                    >
                                                        {formatMoney(allActReg)}
                                                    </td>
                                                    <td
                                                        style={{
                                                            ...cellStyle,
                                                            ...summaryActBg,
                                                            textAlign: 'right',
                                                        }}
                                                    >
                                                        {formatMoney(allActOt)}
                                                    </td>
                                                    <td
                                                        style={{
                                                            ...cellStyle,
                                                            ...summaryActTotalBg,
                                                            textAlign: 'right',
                                                        }}
                                                    >
                                                        {formatMoney(
                                                            allActReg +
                                                                allActOt,
                                                        )}
                                                    </td>
                                                </>
                                            )}
                                        </>
                                    )
                                })()}
                                <td
                                    style={{
                                        ...cellStyle,
                                        textAlign: 'center',
                                        padding: 4,
                                        verticalAlign: 'middle',
                                    }}
                                >
                                    {onRemoveEmployee && (
                                        <Tooltip title="Remove employee from payroll">
                                            <IconButton
                                                size="small"
                                                onClick={() =>
                                                    onRemoveEmployee(emp)
                                                }
                                                style={{ padding: 4 }}
                                                aria-label="Remove employee"
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </td>
                            </tr>
                        )
                    })}
                </tbody>
                <tfoot>
                    <tr style={{ backgroundColor: '#e3f2fd', fontWeight: 600 }}>
                        <td
                            style={{
                                ...stickyLeft(0, '#e3f2fd'),
                                textAlign: 'left',
                                zIndex: 4,
                            }}
                        >
                            Total
                        </td>
                        <td
                            style={{
                                ...stickyLeft(
                                    PAYROLL_LEFT_STAFF_TYPE,
                                    '#e3f2fd',
                                ),
                                textAlign: 'center',
                                zIndex: 4,
                            }}
                        />
                        <td
                            style={{
                                ...stickyLeft(
                                    PAYROLL_LEFT_ON_SALARY,
                                    '#e3f2fd',
                                ),
                                textAlign: 'center',
                                zIndex: 4,
                            }}
                        />
                        <td
                            style={{
                                ...stickyLeft(PAYROLL_LEFT_REGULAR, '#e3f2fd'),
                                textAlign: 'right',
                                zIndex: 4,
                            }}
                        />
                        <td
                            style={{
                                ...stickyLeft(
                                    PAYROLL_LEFT_OVERTIME,
                                    '#e3f2fd',
                                    true,
                                ),
                                textAlign: 'right',
                                zIndex: 4,
                            }}
                        />
                        {weeks.map((week) => {
                            const employees = (
                                week.employees ?? []
                            ).filter((e) => rosterIdSet.has(e.id))
                            const totals = employees.reduce(
                                (acc, emp) => {
                                    const isSalary = emp.on_salary
                                    const wt = emp.week_totals
                                    const regRate = parseFloat(
                                        emp.regular_rate || '0',
                                    )
                                    const otRate = isSalary
                                        ? 0
                                        : parseFloat(emp.overtime_rate || '0')
                                    const projectedTotal = emp.day_entries.reduce(
                                        (s, d) =>
                                            s +
                                            parseFloat(
                                                d.projected_hours || '0',
                                            ),
                                        0,
                                    )
                                    const actualTotal = emp.day_entries.reduce(
                                        (s, d) =>
                                            s +
                                            parseFloat(d.actual_hours || '0'),
                                        0,
                                    )
                                    const projectedReg = Math.min(
                                        projectedTotal,
                                        regHoursThreshold,
                                    )
                                    const projectedOt = isSalary
                                        ? 0
                                        : Math.max(
                                              0,
                                              projectedTotal -
                                                  regHoursThreshold,
                                          )
                                    const actualRegComputed = Math.min(
                                        actualTotal,
                                        regHoursThreshold,
                                    )
                                    const actualOtComputed = isSalary
                                        ? 0
                                        : Math.max(
                                              0,
                                              actualTotal - regHoursThreshold,
                                          )
                                    const projRegPayStored = parseFloat(
                                        wt?.projected_reg_pay ?? '',
                                    )
                                    const projOtPayStored = isSalary
                                        ? 0
                                        : parseFloat(wt?.projected_ot_pay ?? '')
                                    const actRegPayStored = parseFloat(
                                        wt?.actual_reg_pay ?? '',
                                    )
                                    const actOtPayStored = isSalary
                                        ? 0
                                        : parseFloat(wt?.actual_ot_pay ?? '')
                                    let projRegHrs = parseFloat(
                                        wt?.projected_reg_hours ?? '',
                                    )
                                    const projOtHrs = isSalary
                                        ? 0
                                        : parseFloat(
                                              wt?.projected_ot_hours ?? '',
                                          )
                                    let actRegHrs = parseFloat(
                                        wt?.actual_reg_hours ?? '',
                                    )
                                    const actOtHrs = isSalary
                                        ? 0
                                        : parseFloat(wt?.actual_ot_hours ?? '')
                                    if (isSalary) {
                                        projRegHrs = Math.min(
                                            Number.isNaN(projRegHrs)
                                                ? 0
                                                : projRegHrs,
                                            regHoursThreshold,
                                        )
                                        actRegHrs = Math.min(
                                            Number.isNaN(actRegHrs)
                                                ? 0
                                                : actRegHrs,
                                            regHoursThreshold,
                                        )
                                    }
                                    const useStoredProjPay =
                                        !Number.isNaN(projRegPayStored) &&
                                        (!Number.isNaN(projOtPayStored) ||
                                            isSalary)
                                    const useStoredActPay =
                                        !Number.isNaN(actRegPayStored) &&
                                        (!Number.isNaN(actOtPayStored) ||
                                            isSalary)
                                    const useStoredProjHrs =
                                        !Number.isNaN(projRegHrs) &&
                                        (!Number.isNaN(projOtHrs) || isSalary)
                                    const useStoredActHrs =
                                        !Number.isNaN(actRegHrs) &&
                                        (!Number.isNaN(actOtHrs) || isSalary)
                                    const projectedRegPay = useStoredProjPay
                                        ? projRegPayStored
                                        : projectedReg * regRate
                                    const projectedOtPay = useStoredProjPay
                                        ? projOtPayStored
                                        : projectedOt * otRate
                                    const actualRegPay = useStoredActPay
                                        ? actRegPayStored
                                        : useStoredActHrs
                                        ? actRegHrs * regRate
                                        : actualRegComputed * regRate
                                    const actualOtPay = useStoredActPay
                                        ? actOtPayStored
                                        : useStoredActHrs
                                        ? actOtHrs * otRate
                                        : actualOtComputed * otRate
                                    const actualReg = useStoredActPay
                                        ? actRegHrs
                                        : useStoredActHrs
                                        ? actRegHrs
                                        : actualRegComputed
                                    const actualOt = useStoredActPay
                                        ? actOtHrs
                                        : useStoredActHrs
                                        ? actOtHrs
                                        : actualOtComputed
                                    const projectedRegHrs = useStoredProjHrs
                                        ? projRegHrs
                                        : projectedReg
                                    const projectedOtHrsVal = useStoredProjHrs
                                        ? projOtHrs
                                        : projectedOt
                                    return {
                                        projectedTotalHrs:
                                            acc.projectedTotalHrs +
                                            projectedTotal,
                                        projectedRegHrs:
                                            acc.projectedRegHrs +
                                            projectedRegHrs,
                                        projectedOtHrs:
                                            acc.projectedOtHrs +
                                            projectedOtHrsVal,
                                        projectedRegPay:
                                            acc.projectedRegPay +
                                            projectedRegPay,
                                        projectedOtPay:
                                            acc.projectedOtPay + projectedOtPay,
                                        actualTotalHrs:
                                            acc.actualTotalHrs + actualTotal,
                                        actualRegHrs:
                                            acc.actualRegHrs + actualReg,
                                        actualOtHrs: acc.actualOtHrs + actualOt,
                                        actualRegPay:
                                            acc.actualRegPay + actualRegPay,
                                        actualOtPay:
                                            acc.actualOtPay + actualOtPay,
                                    }
                                },
                                {
                                    projectedTotalHrs: 0,
                                    projectedRegHrs: 0,
                                    projectedOtHrs: 0,
                                    projectedRegPay: 0,
                                    projectedOtPay: 0,
                                    actualTotalHrs: 0,
                                    actualRegHrs: 0,
                                    actualOtHrs: 0,
                                    actualRegPay: 0,
                                    actualOtPay: 0,
                                },
                            )
                            const days = week.days ?? []
                            if (days.length === 0) {
                                return (
                                    <td
                                        key={week.id}
                                        style={{ ...cellStyle }}
                                    />
                                )
                            }
                            const projectedTotalPay =
                                totals.projectedRegPay + totals.projectedOtPay
                            const actualTotalPay =
                                totals.actualRegPay + totals.actualOtPay
                            return (
                                <React.Fragment key={week.id}>
                                    {showProjected && (
                                        <>
                                            {days.map((day) => (
                                                <td
                                                    key={day.id}
                                                    style={{
                                                        ...cellStyle,
                                                        ...footProjBand,
                                                        textAlign: 'right',
                                                    }}
                                                />
                                            ))}
                                            <td
                                                style={{
                                                    ...cellStyle,
                                                    ...footProjBand,
                                                    textAlign: 'right',
                                                }}
                                            >
                                                {totals.projectedTotalHrs.toFixed(
                                                    1,
                                                )}
                                            </td>
                                            <td
                                                style={{
                                                    ...cellStyle,
                                                    ...footProjBand,
                                                    textAlign: 'right',
                                                }}
                                            >
                                                {totals.projectedRegHrs.toFixed(
                                                    1,
                                                )}
                                            </td>
                                            <td
                                                style={{
                                                    ...cellStyle,
                                                    ...footProjBand,
                                                    textAlign: 'right',
                                                }}
                                            >
                                                {totals.projectedOtHrs.toFixed(
                                                    1,
                                                )}
                                            </td>
                                            <td
                                                style={{
                                                    ...cellStyle,
                                                    ...footProjBand,
                                                    textAlign: 'right',
                                                }}
                                            >
                                                {formatMoney(
                                                    totals.projectedRegPay,
                                                )}
                                            </td>
                                            <td
                                                style={{
                                                    ...cellStyle,
                                                    ...footProjBand,
                                                    textAlign: 'right',
                                                }}
                                            >
                                                {formatMoney(
                                                    totals.projectedOtPay,
                                                )}
                                            </td>
                                            <td
                                                style={{
                                                    ...cellStyle,
                                                    ...footProjStrong,
                                                    textAlign: 'right',
                                                }}
                                            >
                                                {formatMoney(projectedTotalPay)}
                                            </td>
                                        </>
                                    )}
                                    {showActual && (
                                        <>
                                            {days.map((day, dayIdx) => (
                                                <td
                                                    key={`a-${day.id}`}
                                                    style={{
                                                        ...cellStyle,
                                                        ...footActBand,
                                                        ...(dayIdx === 0
                                                            ? sectionDividerLeft
                                                            : {}),
                                                        textAlign: 'right',
                                                    }}
                                                />
                                            ))}
                                            <td
                                                style={{
                                                    ...cellStyle,
                                                    ...footActBand,
                                                    textAlign: 'right',
                                                }}
                                            >
                                                {totals.actualTotalHrs.toFixed(
                                                    1,
                                                )}
                                            </td>
                                            <td
                                                style={{
                                                    ...cellStyle,
                                                    ...footActBand,
                                                    textAlign: 'right',
                                                }}
                                            >
                                                {totals.actualRegHrs.toFixed(1)}
                                            </td>
                                            <td
                                                style={{
                                                    ...cellStyle,
                                                    ...footActBand,
                                                    textAlign: 'right',
                                                }}
                                            >
                                                {totals.actualOtHrs.toFixed(1)}
                                            </td>
                                            <td
                                                style={{
                                                    ...cellStyle,
                                                    ...footActBand,
                                                    textAlign: 'right',
                                                }}
                                            >
                                                {formatMoney(
                                                    totals.actualRegPay,
                                                )}
                                            </td>
                                            <td
                                                style={{
                                                    ...cellStyle,
                                                    ...footActBand,
                                                    textAlign: 'right',
                                                }}
                                            >
                                                {formatMoney(
                                                    totals.actualOtPay,
                                                )}
                                            </td>
                                            <td
                                                style={{
                                                    ...cellStyle,
                                                    ...footActStrong,
                                                    textAlign: 'right',
                                                }}
                                            >
                                                {formatMoney(actualTotalPay)}
                                            </td>
                                        </>
                                    )}
                                </React.Fragment>
                            )
                        })}
                        {(() => {
                            let allProjReg = 0
                            let allProjOt = 0
                            let allActReg = 0
                            let allActOt = 0
                            weeks.forEach((week) => {
                                const employees = (
                                    week.employees ?? []
                                ).filter((e) => rosterIdSet.has(e.id))
                                employees.forEach((emp) => {
                                    const isSalary = emp.on_salary
                                    const wt = emp.week_totals
                                    const regRate = parseFloat(
                                        emp.regular_rate || '0',
                                    )
                                    const otRate = isSalary
                                        ? 0
                                        : parseFloat(emp.overtime_rate || '0')
                                    const entries = emp.day_entries ?? []
                                    const projTotal = entries.reduce(
                                        (s, d) =>
                                            s +
                                            parseFloat(
                                                d.projected_hours || '0',
                                            ),
                                        0,
                                    )
                                    const actTotal = entries.reduce(
                                        (s, d) =>
                                            s +
                                            parseFloat(d.actual_hours || '0'),
                                        0,
                                    )
                                    const projRegPayStored = parseFloat(
                                        wt?.projected_reg_pay ?? '',
                                    )
                                    const projOtPayStored = isSalary
                                        ? 0
                                        : parseFloat(wt?.projected_ot_pay ?? '')
                                    const actRegPayStored = parseFloat(
                                        wt?.actual_reg_pay ?? '',
                                    )
                                    const actOtPayStored = isSalary
                                        ? 0
                                        : parseFloat(wt?.actual_ot_pay ?? '')
                                    let actRegHrs = parseFloat(
                                        wt?.actual_reg_hours ?? '',
                                    )
                                    const actOtHrs = isSalary
                                        ? 0
                                        : parseFloat(wt?.actual_ot_hours ?? '')
                                    if (isSalary && !Number.isNaN(actRegHrs)) {
                                        actRegHrs = Math.min(
                                            actRegHrs,
                                            regHoursThreshold,
                                        )
                                    }
                                    const useStoredProj =
                                        !Number.isNaN(projRegPayStored) &&
                                        (!Number.isNaN(projOtPayStored) ||
                                            isSalary)
                                    const useStoredAct =
                                        !Number.isNaN(actRegPayStored) &&
                                        (!Number.isNaN(actOtPayStored) ||
                                            isSalary)
                                    const useStoredActHrs =
                                        !Number.isNaN(actRegHrs) &&
                                        (!Number.isNaN(actOtHrs) || isSalary)
                                    if (useStoredProj) {
                                        allProjReg += projRegPayStored
                                        allProjOt += isSalary
                                            ? 0
                                            : projOtPayStored
                                    } else {
                                        const projReg = Math.min(
                                            projTotal,
                                            regHoursThreshold,
                                        )
                                        const projOt = isSalary
                                            ? 0
                                            : Math.max(
                                                  0,
                                                  projTotal - regHoursThreshold,
                                              )
                                        allProjReg += projReg * regRate
                                        allProjOt += projOt * otRate
                                    }
                                    if (useStoredAct) {
                                        allActReg += actRegPayStored
                                        allActOt += isSalary
                                            ? 0
                                            : actOtPayStored
                                    } else if (useStoredActHrs) {
                                        allActReg += actRegHrs * regRate
                                        allActOt += actOtHrs * otRate
                                    } else {
                                        const actReg = Math.min(
                                            actTotal,
                                            regHoursThreshold,
                                        )
                                        const actOt = isSalary
                                            ? 0
                                            : Math.max(
                                                  0,
                                                  actTotal - regHoursThreshold,
                                              )
                                        allActReg += actReg * regRate
                                        allActOt += actOt * otRate
                                    }
                                })
                            })
                            return (
                                <>
                                    {showProjected && (
                                        <>
                                            <td
                                                style={{
                                                    ...cellStyle,
                                                    ...summaryProjBg,
                                                    textAlign: 'right',
                                                }}
                                            >
                                                {formatMoney(allProjReg)}
                                            </td>
                                            <td
                                                style={{
                                                    ...cellStyle,
                                                    ...summaryProjBg,
                                                    textAlign: 'right',
                                                }}
                                            >
                                                {formatMoney(allProjOt)}
                                            </td>
                                            <td
                                                style={{
                                                    ...cellStyle,
                                                    ...summaryProjTotalBg,
                                                    textAlign: 'right',
                                                }}
                                            >
                                                {formatMoney(
                                                    allProjReg + allProjOt,
                                                )}
                                            </td>
                                        </>
                                    )}
                                    {showActual && (
                                        <>
                                            <td
                                                style={{
                                                    ...cellStyle,
                                                    ...summaryActBg,
                                                    ...sectionDividerLeft,
                                                    textAlign: 'right',
                                                }}
                                            >
                                                {formatMoney(allActReg)}
                                            </td>
                                            <td
                                                style={{
                                                    ...cellStyle,
                                                    ...summaryActBg,
                                                    textAlign: 'right',
                                                }}
                                            >
                                                {formatMoney(allActOt)}
                                            </td>
                                            <td
                                                style={{
                                                    ...cellStyle,
                                                    ...summaryActTotalBg,
                                                    textAlign: 'right',
                                                }}
                                            >
                                                {formatMoney(
                                                    allActReg + allActOt,
                                                )}
                                            </td>
                                        </>
                                    )}
                                </>
                            )
                        })()}
                        <td style={{ ...cellStyle }} />
                    </tr>
                </tfoot>
            </table>
        </Box>
    )
}

export const PayrollTracker = (props: Props) => {
    const { scheduleDetail, onPayrollTotalChange, portfolio } = props
    const scheduleId = scheduleDetail?.id
    const payrollOpts = useMemo(
        () => (portfolio ? { portfolio: true as const } : undefined),
        [portfolio],
    )

    const [worksheet, setWorksheet] = useState<PayrollWorksheet | null>(null)
    const [loading, setLoading] = useState(true)
    const [
        staffTypeFilter,
        setStaffTypeFilter,
    ] = useState<PayrollStaffTypeFilter>('all')
    const [hoursViewMode, setHoursViewMode] = useState<PayrollHoursViewMode>(
        'actual',
    )
    const [
        employeeToRemove,
        setEmployeeToRemove,
    ] = useState<PayrollWorksheetEmployee | null>(null)
    const [settingsOpen, setSettingsOpen] = useState(false)
    const [settingsSaving, setSettingsSaving] = useState(false)
    const [payWeekStartWeekday, setPayWeekStartWeekday] = useState(4)

    const payrollTotal = React.useMemo(() => computePayrollTotal(worksheet), [
        worksheet,
    ])
    React.useEffect(() => {
        onPayrollTotalChange?.(payrollTotal)
    }, [payrollTotal, onPayrollTotalChange])

    const fetchWorksheet = useCallback(() => {
        if (!scheduleId) return
        setLoading(true)
        getWorksheetsBySchedule(scheduleId, payrollOpts)
            .then((res) => {
                const data = res.data
                const list = Array.isArray(data)
                    ? data
                    : (data as { results?: { id: number }[] }).results ?? []
                if (list.length > 0) {
                    const firstId = (list[0] as { id: number }).id
                    return getWorksheet(firstId, payrollOpts).then((r) => {
                        setWorksheet(r.data)
                    })
                }
                return generateFromSchedule(
                    { schedule: scheduleId, days_per_week: 7 },
                    payrollOpts,
                ).then((genRes) => {
                    setWorksheet(genRes.data)
                })
            })
            .catch((err) => {
                toast.error(
                    err?.response?.status === 404 ||
                        err?.response?.status === 400
                        ? 'Could not set up payroll. Schedule may have no date range.'
                        : 'Failed to load payroll worksheet',
                )
                setWorksheet(null)
            })
            .finally(() => setLoading(false))
    }, [scheduleId, payrollOpts])

    const loadWorksheet = (id: number) => {
        getWorksheet(id, payrollOpts)
            .then((res) => setWorksheet(res.data))
            .catch(() => toast.error('Failed to load worksheet'))
    }

    useEffect(() => {
        fetchWorksheet()
    }, [fetchWorksheet])

    const handleGenerateWeekDays = (weekId: number, numDays: number) => {
        generateWeekDays(weekId, { num_days: numDays }, payrollOpts)
            .then(() => {
                if (worksheet) loadWorksheet(worksheet.id)
                toast.success('Days generated')
            })
            .catch((err) => {
                const msg =
                    err?.response?.data?.message ||
                    err?.response?.data?.key ||
                    'Failed to generate days'
                toast.error(
                    typeof msg === 'string' ? msg : 'Failed to generate days',
                )
            })
    }

    const handleDayChange = (
        worksheetId: number,
        employee: PayrollWorksheetEmployee,
        dayEntry: PayrollWorksheetEmployeeDay,
        field: 'projected_hours' | 'actual_hours',
        value: string,
    ) => {
        const num = parseFloat(value) || 0
        const strVal = num === 0 ? '0' : value

        setWorksheet((prev) =>
            prev
                ? applyDayEntryUpdate(
                      prev,
                      employee.id,
                      dayEntry.worksheet_day,
                      field,
                      strVal,
                  )
                : prev,
        )

        if (dayEntry.id) {
            updateEmployeeDay(dayEntry.id, { [field]: strVal }, payrollOpts)
                .then(() => loadWorksheet(worksheetId))
                .catch(() => {
                    loadWorksheet(worksheetId)
                    toast.error('Failed to update')
                })
        } else {
            createEmployeeDay(
                {
                    employee: employee.id,
                    worksheet_day: dayEntry.worksheet_day,
                    projected_hours: field === 'projected_hours' ? strVal : '0',
                    actual_hours: field === 'actual_hours' ? strVal : '0',
                },
                payrollOpts,
            )
                .then(() => loadWorksheet(worksheetId))
                .catch(() => {
                    loadWorksheet(worksheetId)
                    toast.error('Failed to save')
                })
        }
    }

    const handleRemoveEmployeeClick = (employee: PayrollWorksheetEmployee) => {
        setEmployeeToRemove(employee)
    }

    const handleOpenSettings = () => {
        setPayWeekStartWeekday(worksheet?.pay_week_start_weekday ?? 4)
        setSettingsOpen(true)
    }

    const handleDownloadCsv = () => {
        if (!worksheet) return
        const csv = buildPayrollCsv(
            worksheet,
            formatMoney,
            getDayWithDate,
            REG_HOURS_THRESHOLD,
        )
        const scheduleName =
            scheduleDetail?.name?.replace(/[^a-z0-9-_]/gi, '-') ?? 'schedule'
        const dateStr = new Date().toISOString().slice(0, 10)
        FileDownload(csv, `payroll-tracker-${scheduleName}-${dateStr}.csv`)
    }

    const handleSavePayWeekStart = () => {
        if (!worksheet) return
        setSettingsSaving(true)
        updateWorksheet(
            worksheet.id,
            { pay_week_start_weekday: payWeekStartWeekday },
            payrollOpts,
        )
            .then(() => getWorksheet(worksheet.id, payrollOpts))
            .then((r) => {
                setWorksheet(r.data)
                setSettingsOpen(false)
                toast.success('Pay week start day updated')
            })
            .catch(() => toast.error('Failed to update settings'))
            .finally(() => setSettingsSaving(false))
    }

    const handleConfirmRemoveEmployee = () => {
        if (!employeeToRemove || !worksheet) return
        const emp = employeeToRemove
        setEmployeeToRemove(null)
        deleteWorksheetEmployee(emp.id, payrollOpts)
            .then(() => {
                loadWorksheet(worksheet.id)
                toast.success('Employee removed from payroll')
            })
            .catch(() => toast.error('Failed to remove employee'))
    }

    const handleEmployeeChange = (
        worksheetId: number,
        employee: PayrollWorksheetEmployee,
        field:
            | 'employee_name'
            | 'staff_type'
            | 'on_salary'
            | 'regular_rate'
            | 'overtime_rate',
        value: string | boolean,
    ) => {
        setWorksheet((prev) =>
            prev ? applyEmployeeUpdate(prev, employee.id, field, value) : prev,
        )
        const payload =
            field === 'regular_rate' || field === 'overtime_rate'
                ? { [field]: String(value) }
                : { [field]: value }
        updateWorksheetEmployee(employee.id, payload, payrollOpts)
            .then(() => loadWorksheet(worksheetId))
            .catch(() => {
                loadWorksheet(worksheetId)
                toast.error('Failed to update employee')
            })
    }

    if (loading && !worksheet) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
            </Box>
        )
    }

    return (
        <Box
            sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                overflow: 'hidden',
            }}
        >
            {!worksheet ? (
                /* No worksheet (e.g. generation failed or schedule has no date range) */
                <Box
                    sx={{
                        flex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        p: 3,
                    }}
                >
                    <Typography color="textSecondary">
                        Could not set up payroll for this schedule. It may have
                        no start or end date.
                    </Typography>
                </Box>
            ) : (
                /* Worksheet exists - one roster, weeks in columns */
                <Box
                    sx={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        minHeight: 0,
                        overflow: 'hidden',
                    }}
                >
                    {/* Header */}
                    <Box
                        sx={{
                            flexShrink: 0,
                            backgroundColor: '#f5f5f5',
                            borderBottom: '1px solid #e0e0e0',
                            py: 1.5,
                            px: 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                        }}
                    >
                        <Typography
                            variant="subtitle1"
                            style={{ fontWeight: 600 }}
                        >
                            Payroll Tracker —{' '}
                            {scheduleDetail?.name ?? 'Schedule'}
                        </Typography>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                            }}
                        >
                            <FormControl
                                size="small"
                                variant="outlined"
                                style={{ minWidth: 168 }}
                            >
                                <InputLabel id="payroll-staff-type-filter-label">
                                    Show staff
                                </InputLabel>
                                <Select
                                    labelId="payroll-staff-type-filter-label"
                                    value={staffTypeFilter}
                                    onChange={(e) =>
                                        setStaffTypeFilter(
                                            e.target
                                                .value as PayrollStaffTypeFilter,
                                        )
                                    }
                                    label="Show staff"
                                >
                                    <MenuItem value="all">All staff</MenuItem>
                                    <MenuItem value="admin">
                                        Admin only
                                    </MenuItem>
                                    <MenuItem value="maintenance">
                                        Maintenance only
                                    </MenuItem>
                                </Select>
                            </FormControl>
                            <ButtonGroup
                                size="small"
                                color="primary"
                                aria-label="Show actual hours, projected hours, or both"
                            >
                                <Button
                                    variant={
                                        hoursViewMode === 'actual'
                                            ? 'contained'
                                            : 'outlined'
                                    }
                                    onClick={() =>
                                        startTransition(() =>
                                            setHoursViewMode('actual'),
                                        )
                                    }
                                >
                                    Actual
                                </Button>
                                <Button
                                    variant={
                                        hoursViewMode === 'projected'
                                            ? 'contained'
                                            : 'outlined'
                                    }
                                    onClick={() =>
                                        startTransition(() =>
                                            setHoursViewMode('projected'),
                                        )
                                    }
                                >
                                    Projected
                                </Button>
                                <Button
                                    variant={
                                        hoursViewMode === 'both'
                                            ? 'contained'
                                            : 'outlined'
                                    }
                                    onClick={() =>
                                        startTransition(() =>
                                            setHoursViewMode('both'),
                                        )
                                    }
                                >
                                    Both
                                </Button>
                            </ButtonGroup>
                            <Tooltip title="Download CSV">
                                <IconButton
                                    size="small"
                                    onClick={handleDownloadCsv}
                                    aria-label="Download payroll CSV"
                                    disabled={!worksheet}
                                >
                                    <GetAppIcon />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Payroll settings">
                                <IconButton
                                    size="small"
                                    onClick={handleOpenSettings}
                                    aria-label="Payroll settings"
                                >
                                    <SettingsIcon />
                                </IconButton>
                            </Tooltip>
                            <Button
                                size="small"
                                variant="contained"
                                color="primary"
                                startIcon={<AddIcon />}
                                onClick={() => {
                                    if (!worksheet) return
                                    createWorksheetEmployee(
                                        {
                                            worksheet: worksheet.id,
                                            employee_name: 'TBD',
                                            staff_type: '',
                                            on_salary: false,
                                            regular_rate: '0',
                                            overtime_rate: '0',
                                            position:
                                                worksheet.employees.length,
                                        },
                                        payrollOpts,
                                    )
                                        .then(() => loadWorksheet(worksheet.id))
                                        .catch(() =>
                                            toast.error(
                                                'Failed to add employee',
                                            ),
                                        )
                                }}
                            >
                                Add Employee
                            </Button>
                        </Box>
                    </Box>

                    <Box
                        sx={{
                            flex: 1,
                            minHeight: 0,
                            border: '1px solid #e0e0e0',
                            borderRadius: 1,
                            p: 1,
                            display: 'flex',
                            flexDirection: 'column',
                        }}
                    >
                        <PayrollGrid
                            worksheet={worksheet}
                            onDayChange={handleDayChange}
                            onEmployeeChange={handleEmployeeChange}
                            onRemoveEmployee={handleRemoveEmployeeClick}
                            onGenerateWeekDays={handleGenerateWeekDays}
                            regHoursThreshold={REG_HOURS_THRESHOLD}
                            formatMoney={formatMoney}
                            getDayWithDate={getDayWithDate}
                            staffTypeFilter={staffTypeFilter}
                            hoursViewMode={hoursViewMode}
                        />
                        <Dialog
                            open={employeeToRemove != null}
                            onClose={() => setEmployeeToRemove(null)}
                            maxWidth="xs"
                            fullWidth
                        >
                            <DialogTitle>
                                Remove employee from payroll?
                            </DialogTitle>
                            <DialogContent>
                                <Typography>
                                    This will remove{' '}
                                    <strong>
                                        {employeeToRemove?.employee_name ??
                                            employeeToRemove?.display_name ??
                                            'this employee'}
                                    </strong>{' '}
                                    from the worksheet and delete all of their
                                    hours across every week. This cannot be
                                    undone.
                                </Typography>
                            </DialogContent>
                            <DialogActions>
                                <Button
                                    onClick={() => setEmployeeToRemove(null)}
                                    color="primary"
                                >
                                    Cancel
                                </Button>
                                <Button
                                    onClick={handleConfirmRemoveEmployee}
                                    color="secondary"
                                    variant="contained"
                                >
                                    Remove
                                </Button>
                            </DialogActions>
                        </Dialog>
                        <Dialog
                            open={settingsOpen}
                            onClose={() =>
                                !settingsSaving && setSettingsOpen(false)
                            }
                            maxWidth="xs"
                            fullWidth
                        >
                            <DialogTitle>Payroll settings</DialogTitle>
                            <DialogContent>
                                <FormControl
                                    fullWidth
                                    size="small"
                                    style={{ marginTop: 8 }}
                                    disabled={settingsSaving}
                                >
                                    <InputLabel>Pay week start day</InputLabel>
                                    <Select
                                        value={payWeekStartWeekday}
                                        label="Pay week start day"
                                        onChange={(e) =>
                                            setPayWeekStartWeekday(
                                                Number(e.target.value),
                                            )
                                        }
                                    >
                                        <MenuItem value={0}>Monday</MenuItem>
                                        <MenuItem value={1}>Tuesday</MenuItem>
                                        <MenuItem value={2}>Wednesday</MenuItem>
                                        <MenuItem value={3}>Thursday</MenuItem>
                                        <MenuItem value={4}>Friday</MenuItem>
                                        <MenuItem value={5}>Saturday</MenuItem>
                                        <MenuItem value={6}>Sunday</MenuItem>
                                    </Select>
                                </FormControl>
                            </DialogContent>
                            <DialogActions>
                                <Button
                                    onClick={() => setSettingsOpen(false)}
                                    color="primary"
                                    disabled={settingsSaving}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    onClick={handleSavePayWeekStart}
                                    color="primary"
                                    variant="contained"
                                    disabled={settingsSaving}
                                    startIcon={
                                        settingsSaving ? (
                                            <CircularProgress
                                                size={16}
                                                color="inherit"
                                            />
                                        ) : null
                                    }
                                >
                                    Save
                                </Button>
                            </DialogActions>
                        </Dialog>
                    </Box>
                </Box>
            )}
        </Box>
    )
}
